import { countries } from 'constants/countries';
import { FieldTypes } from 'constants/field-types-enum';
import { meetsLogic } from 'constants/logic';
import i18n from 'i18n';
import moment from 'moment';

import { FilePreview } from 'components/FilePreview/FilePreview';
import {
  Address,
  Checkbox,
  DatePicker,
  DateSelector,
  DocumentUpload,
  DownloadField,
  DynamicArrayField,
  Email,
  FilePicker,
  FixedArray,
  IdRequirementsList,
  IdRequirementsWrapper,
  MultipleChoice,
  MultipleDocumentUpload,
  MultipleSelect,
  NumberInput,
  OpinionScale,
  OptionsList,
  PhoneNumberPicker,
  Select,
  Signature,
  StarRating,
  Text,
  Textarea,
  ToggleSwitch,
  Website,
} from 'components/FormComponents';

export const noHeaderComponents = [
  FieldTypes.Checkbox,
  FieldTypes.Consent,
  FieldTypes.Download,
  FieldTypes.PDFPreview,
  FieldTypes.Statement,
  FieldTypes.ToggleSwitch,
];

export const componentMap = {
  [FieldTypes.FreeText]: Textarea,
  [FieldTypes.MultipleChoice]: MultipleChoice,
  [FieldTypes.YesNo]: MultipleChoice,
  [FieldTypes.OpinionScale]: OpinionScale,
  [FieldTypes.Date]: DatePicker,
  [FieldTypes.DateSelector]: DateSelector,
  [FieldTypes.PhoneNumber]: PhoneNumberPicker,
  [FieldTypes.StarRating]: StarRating,
  [FieldTypes.Upload]: FilePicker,
  [FieldTypes.Signature]: Signature,
  [FieldTypes.Country]: Select,
  [FieldTypes.Select]: Select,
  [FieldTypes.MultiSelect]: MultipleSelect,
  [FieldTypes.Text]: Textarea,
  [FieldTypes.Number]: NumberInput,
  [FieldTypes.IdRequirementsList]: IdRequirementsList,
  [FieldTypes.Checkbox]: Checkbox,
  [FieldTypes.ShortText]: Text,
  [FieldTypes.Email]: Email,
  [FieldTypes.Address]: Address,
  [FieldTypes.DynamicArray]: DynamicArrayField,
  [FieldTypes.FixedArray]: FixedArray,
  [FieldTypes.DocumentUpload]: DocumentUpload,
  [FieldTypes.MultiUpload]: MultipleDocumentUpload,
  [FieldTypes.IdRequirementsWrapper]: IdRequirementsWrapper,
  [FieldTypes.Consent]: Checkbox,
  [FieldTypes.Email]: Email,
  [FieldTypes.Website]: Website,
  [FieldTypes.Download]: DownloadField,
  [FieldTypes.ReadOnly]: Text,
  [FieldTypes.Hidden]: Text,
  [FieldTypes.PDFPreview]: FilePreview,
  [FieldTypes.ToggleSwitch]: ToggleSwitch,
  [FieldTypes.OptionsList]: OptionsList,
};

const translatePlaceholder = placeholder =>
  placeholder ? i18n.t(placeholder, { defaultValue: placeholder }) : placeholder;

export const getComponentProps = ({
  question,
  value,
  candidateId,
  handlers = {},
  brand,
  notForForm = false,
  fieldPathIndex = {},
  allValues = {},
  noMargin = false,
  uploads = [],
}) => {
  switch (question.type) {
    case FieldTypes.FreeText:
      return {
        controlledInput: false,
        placeholder: translatePlaceholder(question?.placeholder),
        autoComplete: question?.autoComplete || 'off',
        minLength: question?.minLength,
        maxLength: question?.maxLength,
        defaultValue: question?.defaultValue,
        noErrorMessage: true,
        required: question?.required,
        noMargin: noMargin,
        rows: question?.rows ?? 6,
      };
    case FieldTypes.Statement:
    case FieldTypes.Text:
    case FieldTypes.ShortText:
    case FieldTypes.Email:
    case FieldTypes.Website:
    case FieldTypes.Address:
      return {
        controlledInput: false,
        placeholder: translatePlaceholder(question?.placeholder),
        autoComplete: question?.autoComplete || 'off',
        minLength: question?.minLength,
        maxLength: question?.maxLength,
        defaultValue: question?.defaultValue,
        noErrorMessage: true,
        required: question?.required,
        noMargin: noMargin,
        rows: question?.type === FieldTypes.Text ? (question?.rows ?? 4) : undefined,
        dataTestId:
          question?.code === 'residential_address' ? 'residential-address-input' : undefined,
      };
    case FieldTypes.Hidden:
      return {
        controlledInput: false,
        defaultValue: question?.defaultValue,
        hidden: true,
      };
    case FieldTypes.Number:
      return {
        controlledInput: false,
        placeholder: translatePlaceholder(question?.placeholder),
        min: question?.min,
        max: question?.max,
        step: question?.step,
        defaultValue: question?.defaultValue,
        noErrorMessage: true,
        required: question?.required,
        noMargin: noMargin,
      };
    case FieldTypes.PhoneNumber:
      return {
        controlledInput: true,
        placeholder: translatePlaceholder(question?.placeholder),
        value: question?.defaultValue,
        defaultCountry: question?.defaultCountry,
        countrySelectTestId: 'phone-number-country-select',
        numberInputTestId: 'phone-number-input',
      };
    case FieldTypes.ReadOnly:
      return {
        controlledInput: false,
        placeholder: translatePlaceholder(question?.placeholder),
        defaultValue: question?.defaultValue,
        readOnly: true,
      };
    case FieldTypes.MultipleChoice:
      return {
        brand,
        options: {
          multipleSelection:
            question.options?.multipleSelection ?? question.options?.multiple_selection,
          otherOption: question.options?.otherOption ?? question.options?.other_option,
          options: question.options?.options,
        },
        defaultValue: question?.defaultValue,
        controlledInput: true,
        noMargin: noMargin,
      };
    case FieldTypes.StarRating:
      return {
        brand,
        options: question.options,
      };
    case FieldTypes.YesNo:
      return {
        brand,
        options: {
          multipleSelection: false,
          otherOption: false,
          options: [
            { value: 'Yes', name: i18n.t('keyboard.hint.yes') },
            { value: 'No', name: i18n.t('keyboard.hint.no') },
          ],
        },
        yesNo: true,
        defaultValue: question?.defaultValue,
        controlledInput: true,
        noMargin: noMargin,
      };
    case FieldTypes.Checkbox:
    case FieldTypes.Consent:
      return {
        brand,
        controlledInput: true,
        label: question?.text || question?.title,
        description: question?.description,
        align: question?.type === FieldTypes.Consent ? 'start' : 'center',
        checked: question?.defaultValue,
        secondary: true,
        modalConfirmation: question?.modalConfirmation,
        modalConfirmationTitle: question?.modalTitle,
        modalConfirmationDescription: question?.modalContent,
        modalConfirmationConfirmText: question?.modalConfirmText,
        modalConfirmationCancelText: question?.modalCancelText,
        modalConfirmationType: question?.modalConfirmationType,
      };
    case FieldTypes.Upload:
    case FieldTypes.DocumentUpload: {
      const upload = (uploads || [])?.find(u => u?.metadata?.field_code === question.code) ?? null;
      const baseMetadata = upload?.metadata || question?.metadata || {};
      const metadataWithFieldCode = {
        ...baseMetadata,
        field_code: question.code,
      };
      const parsedFormFieldId = Number(question.id);
      const hasNumericFormFieldId =
        Number.isInteger(parsedFormFieldId) && String(parsedFormFieldId) === String(question.id);
      return {
        accept: {
          'application/pdf': ['.pdf'],
          'application/msword': ['.doc', '.docx'],
          'image/*': [],
        },
        modelParams: {
          ...(hasNumericFormFieldId ? { form_field_id: parsedFormFieldId } : {}),
          candidate_id: candidateId,
        },
        metadata: metadataWithFieldCode,
        documentTitle: question?.text || question?.title || '',
        showTitleInUploadSlot: false,
        getUpload: handlers?.getCandidateUpload,
        signUpload: handlers?.signCandidateUpload,
        createUpload: handlers?.createCandidateUpload,
        updateUpload: handlers?.updateCandidateUpload,
        deleteUpload: handlers?.deleteCandidateUpload,
        value: upload?.id ?? null,
        dataTestId: 'question-upload-filename',
      };
    }
    case FieldTypes.MultiUpload: {
      const parsedMultiUploadFieldId = Number(question.id);
      const hasNumericMultiUploadFieldId =
        Number.isInteger(parsedMultiUploadFieldId) &&
        String(parsedMultiUploadFieldId) === String(question.id);
      return {
        accept: question.accept || {
          'application/pdf': ['.pdf'],
          'application/msword': ['.doc', '.docx'],
          'image/*': [],
        },
        modelParams: {
          ...(hasNumericMultiUploadFieldId ? { form_field_id: parsedMultiUploadFieldId } : {}),
          candidate_id: candidateId,
        },
        metadata: question?.metadata || question?.runtime_config?.metadata || {},
        bucketFolder: question?.metadata?.bucket_folder,
        idType: question?.metadata?.id_type ?? question?.runtime_config?.metadata?.id_type ?? '',
        getUpload: handlers?.getCandidateUpload,
        signUpload: handlers?.signCandidateUpload,
        createUpload: handlers?.createCandidateUpload,
        updateUpload: handlers?.updateCandidateUpload,
        deleteUpload: handlers?.deleteCandidateUpload,
        verifyDocument: false,
        useModel: true,
        // null (not []) when no external uploads list is threaded in (e.g. Form.jsx callers)
        // so QuestionWrapper's MultiUpload override falls back to the real RHF-tracked value
        // instead of permanently masking it with a stray empty array.
        value: uploads.length ? uploads : null,
      };
    }
    case FieldTypes.Signature:
      // Extract ID from value if it's an object, otherwise use value directly (or null/undefined)
      // This prevents passing objects to getUpload which expects an ID
      const signatureIdValue =
        value && typeof value === 'object' ? (value.id !== undefined ? value.id : null) : value;
      return {
        modelParams: {
          ...(notForForm ? {} : { form_field_id: question.id }),
          candidate_id: candidateId,
        },
        signatureId: signatureIdValue,
        metadata: question?.metadata || {},
        getUpload: handlers?.getCandidateUpload,
        signUpload: handlers?.signCandidateUpload,
        createUpload: handlers?.createCandidateUpload,
        updateUpload: handlers?.updateCandidateUpload,
        deleteUpload: handlers?.deleteCandidateUpload,
      };
    case FieldTypes.OpinionScale:
      return {
        brand,
        options: question.options,
      };
    case FieldTypes.Date:
      return {
        options: question.options,
        useDefaultToday:
          typeof question.useDefaultToday === 'boolean'
            ? question.useDefaultToday
            : question.defaultValue == null || question.defaultValue === ''
              ? true
              : !!question.options?.useDefault,
        controlledInput: true,
        noErrorMessage: true,
        // Backend (field_resolver) sends minDate/maxDate as d/m/Y strings; DatePicker expects Date instances
        minDate: question.minDate ? moment(question.minDate, 'DD/MM/YYYY').toDate() : undefined,
        maxDate: question.maxDate ? moment(question.maxDate, 'DD/MM/YYYY').toDate() : undefined,
      };
    case FieldTypes.DateSelector:
      return {
        controlledInput: true,
        options: question?.options,
        noDay: question?.options?.noDay || question?.noDay || false,
        firstYear: question?.options?.firstYear || question?.firstYear,
        lastYear: question?.options?.lastYear || question?.lastYear,
        min: question?.options?.min || question?.min,
        max: question?.options?.max || question?.max,
        outputFormat: question?.options?.outputFormat || question?.outputFormat || 'DD/MM/YYYY',
        noErrorMessage: true,
      };
    case FieldTypes.Country:
      return {
        useDefault: true,
        defaultValue: question?.defaultValue,
        placeholder: translatePlaceholder(question?.placeholder),
        children: countries.map(country => (
          <Select.Item key={country.value} value={country.value}>
            {country.name}
          </Select.Item>
        )),
      };
    case FieldTypes.Select:
      return {
        controlledInput: true,
        placeholder: translatePlaceholder(question?.placeholder),
        children: Array.isArray(question?.options)
          ? question.options.map(option => (
              <Select.Item key={option.value} value={option.value}>
                {option.name}
              </Select.Item>
            ))
          : null,
        options: question?.options,
        useSearch: question?.useSearch,
        useDefault: question?.useDefault,
        defaultValue: question?.defaultValue,
        noErrorMessage: true,
      };
    case FieldTypes.MultiSelect:
      return {
        controlledInput: true,
        placeholder: translatePlaceholder(question?.placeholder),
        children: Array.isArray(question?.options)
          ? question.options.map(option => (
              <MultipleSelect.Item key={option.value} value={option.value}>
                {option.name}
              </MultipleSelect.Item>
            ))
          : null,
        minSelect: question?.min !== null ? Number(question?.min) : undefined,
        maxSelect: question?.max !== null ? Number(question?.max) : undefined,
        options: question?.options,
        useSearch: question?.useSearch,
        useDefault: question?.useDefault,
        defaultValue: question?.defaultValue,
        noErrorMessage: true,
      };
    case FieldTypes.IdRequirementsWrapper:
      return {
        brand,
        uploadCode: candidateId,
        field: question,
        getUpload: handlers?.getCandidateUpload,
        signUpload: handlers?.signCandidateUpload,
        createUpload: handlers?.createCandidateUpload,
        updateUpload: handlers?.updateCandidateUpload,
        deleteUpload: handlers?.deleteCandidateUpload,
        modelParams: { candidate_id: candidateId },
        componentMap,
        controlledInput: false, // This is a wrapper component, not a form input
        fieldPathIndex,
        allValues,
        uploads,
      };
    case FieldTypes.IdRequirementsList:
      return {
        brand,
        uploadCode: candidateId,
        options: question.options,
        requirementsMet: Boolean(question.requirementsMet),
        min: Number.isFinite(question.min) ? question.min : null,
        minPoints: Number.isFinite(question.minPoints) ? question.minPoints : 0,
        getUpload: handlers?.getCandidateUpload,
        signUpload: handlers?.signCandidateUpload,
        createUpload: handlers?.createCandidateUpload,
        updateUpload: handlers?.updateCandidateUpload,
        deleteUpload: handlers?.deleteCandidateUpload,
        modelParams: { candidate_id: candidateId },
      };
    case FieldTypes.DynamicArray:
      return {
        brand,
        subFields: question.subFields,
        arrayItemTitles: question.arrayItemTitles,
        max: question.max,
        controlledInput: false, // This tells QuestionWrapper not to wrap in Controller
        // Pass the necessary parameters for DynamicArrayField to create its own getComponentProps
        candidateId,
        handlers,
        fieldPathIndex,
        allValues,
        uploads,
      };
    case FieldTypes.FixedArray:
      return {
        brand,
        subFields: question.subFields,
        arrayItemTitles: question.arrayItemTitles,
        size: question.size,
        controlledInput: false, // This tells QuestionWrapper not to wrap in Controller
        // Pass the necessary parameters for FixedArray to create its own getComponentProps
        candidateId,
        handlers,
        fieldPathIndex,
        allValues,
        uploads,
      };
    case FieldTypes.Download:
      return {
        controlledInput: false,
        uploadId: question?.options?.uploadId ?? question?.options?.upload_id,
        getCandidateUpload: handlers?.getCandidateUpload,
        downloadUrl: question?.downloadUrl ?? question?.download_url,
        fileName: question?.fileName ?? question?.file_name,
        title: question?.options?.title ?? question?.title,
        description: question?.options?.description ?? question?.description,
      };
    case FieldTypes.PDFPreview:
      return {
        // Support both camelCase and snake_case for URL (env/CDN changes may affect source)
        file: {
          url: question?.downloadUrl ?? question?.download_url,
          file_type: 'application/pdf',
        },
        zoom: true,
        enableZoom: false,
        displayMetadata: false,
      };
    case FieldTypes.ToggleSwitch:
      const toggleDefaultValue =
        question?.defaultValue !== undefined && question?.defaultValue !== null
          ? Boolean(question.defaultValue)
          : false;
      return {
        controlledInput: false,
        label: question?.title || question?.text,
        defaultValue: toggleDefaultValue,
        noMargin: noMargin,
      };
    case FieldTypes.OptionsList:
      return {
        controlledInput: false,
        arrayItemTitles: question?.arrayItemTitles,
        defaultValue: question?.defaultValue,
        noMargin: noMargin,
      };
    default:
      return question;
  }
};

export const handleLogicJump = (
  formValues,
  logicJumps,
  currentQuestion,
  currentQuestionValue,
  skippedQuestions,
  filteredQuestions,
) => {
  // Get all jumps controlled by the current question
  // The first condition is for the case where the jump is controlled by the current question `from`
  // The second condition is for the case where the jump is controlled by the different question `field` e.g. Q1 = X then Q4 jumps to Q6
  const jumpsControlledByCurrent = logicJumps.filter(
    jump => jump.from === currentQuestion.order || jump.field === currentQuestion.order,
  );

  // Clean all the skipped questions that are controlled by the current question
  // This is useful when the user goes back to a question that has a jump and changes the value
  // so that the jump is no longer valid, we don't want to skip that question
  // The third condition handles cases where the jump is controlled by the current question's to value — for example, if Q3 jumps to Q5. If Q4 is set to 'X', it will jump to Q6. However, if the user goes back and changes the value of Q3, we need to restore Q5, even though it was previously cleared, because Q3 now skips Q4.
  const cleanedSkipped = new Set(
    Array.from(skippedQuestions).filter(
      order =>
        !jumpsControlledByCurrent.some(jump => {
          const jumpTo = Number(jump.to);
          if (jumpTo === -1) return order > jump.from;
          return (order >= jump.from && order < jumpTo) || order === jumpTo;
        }),
    ),
  );

  for (const jump of jumpsControlledByCurrent) {
    // Get the value of the jump from the current question order or the different question field because the jump is controlled by the different question field
    const jumpValue =
      jump.field === currentQuestion.order
        ? currentQuestionValue
        : formValues[filteredQuestions.find(q => q.order === jump.field)?.id];
    if (meetsLogic(jump, jumpValue)) {
      let rangeToSkip;
      const jumpTo = Number(jump.to);
      if (jumpTo === -1) {
        // Skip everything after from until the end
        const maxOrder = Math.max(...filteredQuestions.map(q => q.order));
        rangeToSkip = new Set(
          Array.from({ length: maxOrder - jump.from }, (_, i) => jump.from + 1 + i),
        );
      } else {
        rangeToSkip = new Set(
          Array.from({ length: jumpTo - jump.from - 1 }, (_, i) => jump.from + 1 + i),
        );
      }

      const newSkipped = new Set([...cleanedSkipped, ...rangeToSkip]);

      if (jump.field !== jump.from) {
        return { jumped: false, newSkipped };
      }

      return { jumped: true, newSkipped, jumpToIndex: jumpTo };
    }
  }

  return {
    jumped: false,
    newSkipped: cleanedSkipped,
  };
};
